// ALTER TABLE clause classifier. Given a single trimmed clause segment of an
// ALTER TABLE statement, identify whether it lands in v1's narrow surface
// (add-column / drop-column / rename-column) or as `opaque` so the round-trip
// preserves the source bytes verbatim.
//
// Per CLAUDE.md the file budget is 200 lines, so the type-text + nullable
// helpers are imported from the CREATE TABLE column parser rather than
// duplicated here.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: DDL Structure Extractor —
//      ALTER TABLE / Index forms. v1 op set locked at DSE3.

import type { ParseOptions, SqlRange } from '../../../sql-grammar/model';
import { readIdentifier } from '../../scope/identifiers';
import type { AlterTableClause, ColumnDef } from '../../types';

import { NON_COLUMN_LEAD_KEYWORDS, parenAddedColumns } from './alterTable.addParen';
import { findKeywordAtTopLevel, skipWsCommentsLocal, tryConsumeKeyword } from './shared';

/** Type-terminator set duplicated minimally for the simple ALTER ADD COLUMN
 *  shape — `ADD COLUMN c TYPE [NULL/NOT NULL]`. */
const TYPE_TERMINATORS: ReadonlySet<string> = new Set([
  'NULL', 'NOT', 'DEFAULT', 'IDENTITY', 'AUTO_INCREMENT', 'AUTOINCREMENT',
  'PRIMARY', 'UNIQUE', 'COLLATE', 'COMMENT', 'CONSTRAINT',
  'INDEX', 'KEY', 'CHECK', 'VISIBLE', 'INVISIBLE',
  'STORED', 'VIRTUAL', 'PERSISTED', 'ROWGUIDCOL', 'SPARSE',
  'ENABLE', 'DISABLE', 'ENCRYPTED', 'MASKED', 'COMPRESSED',
  'ON', 'CHARACTER',
]);

function readTypeText(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): { readonly text: string; readonly endOffset: number } {
  const hit = findKeywordAtTopLevel(source, options, start, end, TYPE_TERMINATORS);
  const stop = hit ? hit.start : end;
  const s = skipWsCommentsLocal(source, start, stop, options);
  let e = stop;
  while (e > s) {
    const ch = source[e - 1];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { e--; continue; }
    break;
  }
  return { text: source.slice(s, e), endOffset: stop };
}

function detectNullable(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): boolean | undefined {
  const notHit = findKeywordAtTopLevel(source, options, start, end, new Set(['NOT']));
  if (notHit) {
    const next = skipWsCommentsLocal(source, notHit.end, end, options);
    const after = readIdentifier(source, next, end, options);
    if (after && !after.quoted && after.value.toUpperCase() === 'NULL') return false;
  }
  if (findKeywordAtTopLevel(source, options, start, end, new Set(['NULL']))) return true;
  return undefined;
}

function classifyAdd(
  source: string,
  options: ParseOptions,
  afterAdd: number,
  end: number,
  range: SqlRange,
): AlterTableClause {
  // Optional `COLUMN` keyword.
  let i = skipWsCommentsLocal(source, afterAdd, end, options);
  const afterColumn = tryConsumeKeyword(source, i, end, options, 'COLUMN');
  if (afterColumn !== i) i = skipWsCommentsLocal(source, afterColumn, end, options);

  // Parenthesised forms (`ADD (col INT, col2 INT)` — Oracle's only
  // multi-column ADD syntax, and a shape MySQL accepts too) stay opaque for
  // v1: multi-column adds aren't in the narrow op set. The NAMES they declare
  // are still recorded, because a LATER edit naming one of those columns has
  // to know whether the source wrote it delimited — see `alterTable.addParen`.
  if (source[i] === '(') {
    return { kind: 'opaque', range, addedColumns: parenAddedColumns(source, options, i, end) };
  }

  // Constraint-shaped clause? → opaque.
  const lead = readIdentifier(source, i, end, options);
  if (!lead) return { kind: 'opaque', range };
  if (!lead.quoted && NON_COLUMN_LEAD_KEYWORDS.has(lead.value.toUpperCase())) {
    return { kind: 'opaque', range };
  }

  // Simple column shape: <name> <type> [NULL/NOT NULL].
  const typeStart = skipWsCommentsLocal(source, lead.end, end, options);
  const { text: typeRef, endOffset: typeEnd } = readTypeText(source, options, typeStart, end);
  if (!typeRef) return { kind: 'opaque', range };
  const nullable = detectNullable(source, options, typeEnd, end);
  const column: ColumnDef = {
    name: lead.value,
    typeRef,
    range,
    ...(nullable !== undefined ? { nullable } : {}),
  };
  return { kind: 'add-column', range, column, nameQuoting: lead.quoting };
}

function classifyDrop(
  source: string,
  options: ParseOptions,
  afterDrop: number,
  end: number,
  range: SqlRange,
): AlterTableClause {
  let i = skipWsCommentsLocal(source, afterDrop, end, options);
  const afterColumn = tryConsumeKeyword(source, i, end, options, 'COLUMN');
  if (afterColumn !== i) i = skipWsCommentsLocal(source, afterColumn, end, options);

  // Constraint-shaped DROP (DROP CONSTRAINT, DROP INDEX, DROP PRIMARY KEY,
  // DROP FOREIGN KEY, DROP CHECK) → opaque.
  const ident = readIdentifier(source, i, end, options);
  if (!ident) return { kind: 'opaque', range };
  if (!ident.quoted && NON_COLUMN_LEAD_KEYWORDS.has(ident.value.toUpperCase())) {
    return { kind: 'opaque', range };
  }
  return { kind: 'drop-column', range, columnName: ident.value };
}

function classifyRename(
  source: string,
  options: ParseOptions,
  afterRename: number,
  end: number,
  range: SqlRange,
): AlterTableClause {
  // RENAME COLUMN <old> TO <new>. Anything else (RENAME TO …, RENAME
  // CONSTRAINT …, RENAME INDEX …) → opaque.
  let i = skipWsCommentsLocal(source, afterRename, end, options);
  const afterColumn = tryConsumeKeyword(source, i, end, options, 'COLUMN');
  if (afterColumn === i) return { kind: 'opaque', range };
  i = skipWsCommentsLocal(source, afterColumn, end, options);

  const oldIdent = readIdentifier(source, i, end, options);
  if (!oldIdent) return { kind: 'opaque', range };
  i = skipWsCommentsLocal(source, oldIdent.end, end, options);

  const afterTo = tryConsumeKeyword(source, i, end, options, 'TO');
  if (afterTo === i) return { kind: 'opaque', range };
  i = skipWsCommentsLocal(source, afterTo, end, options);

  const newIdent = readIdentifier(source, i, end, options);
  if (!newIdent) return { kind: 'opaque', range };
  return {
    kind: 'rename-column', range,
    oldName: oldIdent.value, newName: newIdent.value,
    newNameQuoting: newIdent.quoting,
  };
}

export function classifyAlterTableClause(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
  range: SqlRange,
): AlterTableClause {
  const lead = readIdentifier(source, start, end, options);
  if (!lead || lead.quoted) return { kind: 'opaque', range };
  const upper = lead.value.toUpperCase();
  if (upper === 'ADD') return classifyAdd(source, options, lead.end, end, range);
  if (upper === 'DROP') return classifyDrop(source, options, lead.end, end, range);
  if (upper === 'RENAME') return classifyRename(source, options, lead.end, end, range);
  return { kind: 'opaque', range };
}
